import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { LcsApiService } from '@lcs/shared/api';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import { catchError, concatMap, exhaustMap, map, switchMap, switchMapTo, tap, withLatestFrom } from 'rxjs/operators';
import { promptsRoute } from 'src/app/app-routing.module';
import { AppState } from 'src/app/app.reducer';
import { selectCurrentRoute, selectRouteParams } from 'src/app/app.selectors';
import { hasSelectedPrivilege, selectPrivileges } from 'src/app/authentication/state/authentication.selector';
import { ConfirmationDialogService } from 'src/app/common-components/confirmation-dialog/confirmation-dialog.service';
import { DialogSize } from 'src/app/common-components/confirmation-dialog/enumerations/dialog-size.enum';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { Privileges } from 'src/app/privileges/privileges';
import { RecordingNewPromptHub } from 'src/app/signalr/recording-new-prompt-hub/recording-new-prompt-hub.service';
import { AddPromptComponent } from '../../add-prompt/containers/add-prompt.component';
import { GetPromptDetail, GetPromptDetailVariables } from '../queries/generated/GetPromptDetail';
import { getPromptDetail } from '../queries/prompt-detail.graphql';
import { PromptDetailApiService } from '../services/prompt-detail-api.service';
import * as PromptDetailActions from './prompt-detail.actions';
@Injectable()
export class PromptDetailEffects {
   loadPromptDetail = createEffect(() => {
      return this.actions$.pipe(
         ofType(
            PromptDetailActions.promptDetailLoad,
            PromptDetailActions.savePromptDetailSuccess,
            PromptDetailActions.promptDetailCancel
         ),
         withLatestFrom(this.store.pipe(select(selectRouteParams))),
         concatMap(([action, params]) => {
            const id = parseInt(params.id, 10);

            return this.apiService
               .query<GetPromptDetail, GetPromptDetailVariables>({
                  query: getPromptDetail,
                  variables: { id },
               })
               .pipe(
                  withLatestFrom(
                     this.store.pipe(select(selectPrivileges)),
                     this.store.pipe(select(selectCurrentRoute))
                  ),
                  map(([promptResult, privileges, route]) => {
                     if (
                        !hasSelectedPrivilege(
                           Privileges.ManageAccountPrompts_View,
                           promptResult.data.prompt.account?.accountId ?? 0,
                           privileges
                        )
                     ) {
                        this.router.navigate(['access-denied'], { skipLocationChange: true }).then(() => {
                           this.location.replaceState(route.url.join('/'));
                        });
                        return PromptDetailActions.promptDetailAccessDenied();
                     }
                     return PromptDetailActions.loadPromptDetailSuccess({
                        data: promptResult.data.prompt,
                     });
                  }),
                  catchError((error) => {
                     return of(PromptDetailActions.loadPromptDetailFailure(error));
                  })
               );
         })
      );
   });

   deleteNewFileIfPromptSaveCanceled$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(PromptDetailActions.deleteNewFileIfPromptSaveCanceled),
         switchMap((action) => {
            return this.deleteNewFile(action.fileId).pipe(
               map(() => {
                  return PromptDetailActions.deleteFileSuccess();
               }),
               catchError((error) => {
                  return of(PromptDetailActions.deleteFileFailure(error));
               })
            );
         })
      );
   });

   savePromptDetail = createEffect(() => {
      return this.actions$.pipe(
         ofType(PromptDetailActions.savePromptDetail),
         switchMap((action) => {
            const apiCall = this.promptDetailApiService.save(action.prompt, action.promptFields, action.file);
            return apiCall.pipe(
               map((result) => {
                  return PromptDetailActions.savePromptDetailSuccess({ data: result.data });
               }),
               catchError((error) => {
                  return of(PromptDetailActions.savePromptDetailFailure(error));
               })
            );
         })
      );
   });

   revertPromptFileUpdateOnNavigateAway$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(PromptDetailActions.revertPromptFileUpdateOnNavigate),
         switchMap((action) => {
            const apiCall = this.promptDetailApiService.revertPromptFileUpdate(action.prompt);
            return apiCall.pipe(
               map(() => {
                  if (action.fileId) {
                     return PromptDetailActions.deleteNewFileIfPromptSaveCanceled({ fileId: action.fileId });
                  } else {
                     return PromptDetailActions.revertPromptFileSuccess();
                  }
               }),
               catchError((error) => {
                  return of(PromptDetailActions.revertPromptFileFailure(error));
               })
            );
         })
      );
   });

   savePromptDetailSuccess = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(PromptDetailActions.savePromptDetailSuccess),
            tap(() => {
               this.snackBar.open('Prompt Saved');
            })
         );
      },
      {
         dispatch: false,
      }
   );

   uploadWrongFileType$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(PromptDetailActions.uploadWrongFileType),
            tap((action) => {
               this.snackBar.open(`File Type Not Accepted. Accepted File types include: ${action.data}`);
            })
         );
      },
      {
         dispatch: false,
      }
   );

   createNewPromptFile$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(PromptDetailActions.changePromptFile),
            exhaustMap((action) => {
               const dialogRef = this.dialogService.openFormComponent(AddPromptComponent, {
                  data: {
                     onlyPromptTypeOption: action.promptType,
                     promptId: action.promptId,
                     name: action.name,
                     accountId: action.accountId,
                     routeToNewPromptAfterSave: false,
                  },
                  disableClose: true,
                  panelClass: ['add-dialog-panel', 'dialog-container'],
               });
               this.dialogId = dialogRef.id;
               return dialogRef.afterClosed().pipe(switchMapTo(EMPTY));
            })
         );
      },
      { dispatch: false }
   );

   deletePrompt = createEffect(() => {
      return this.actions$.pipe(
         ofType(PromptDetailActions.deletePrompt),
         switchMap((action) => {
            const message = 'Are you sure you would like to delete this Prompt?';

            const dialogRef = this.confirmationService.confirm({
               data: {
                  header: 'Delete Prompt?',
                  message,
               },
               size: DialogSize.Medium,
            });

            return dialogRef.afterClosed().pipe(
               switchMap((confirmed) => {
                  if (confirmed) {
                     return this.promptDetailApiService.deletePrompt(action.promptId).pipe(
                        map(() => {
                           return PromptDetailActions.deletePromptSuccess(action);
                        }),
                        catchError((error) => {
                           return of(PromptDetailActions.deletePromptFailure(error));
                        })
                     );
                  }
                  return EMPTY;
               })
            );
         })
      );
   });

   private deleteNewFile(fileId: number | null) {
      return this.promptDetailApiService.deleteFile(fileId);
   }

   deletePromptSuccess = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(PromptDetailActions.deletePromptSuccess),
            tap((action) => {
               this.router.navigate([promptsRoute]);
               this.snackBar.open(`Prompt ${action.name} deleted`);
            })
         );
      },
      { dispatch: false }
   );

   dialogId: string;

   constructor(
      private actions$: Actions,
      private apiService: LcsApiService,
      private confirmationService: ConfirmationDialogService,
      private store: Store<AppState>,
      private promptDetailApiService: PromptDetailApiService,
      private snackBar: MatSnackBar,
      private router: Router,
      private recordingNewPromptHub: RecordingNewPromptHub,
      private location: Location,
      private dialogService: LcsDialogService
   ) {}
}
